import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  DataRoomNodeAccessState,
  DataRoomNodeWithStateAndSignal,
  Lead,
} from 'models/Lead';
import DisplayFilesByLeadCategory from './components/DisplayFilesByLeadCategory';
import { getArrayLength } from 'utils';
import useReleaseFileForLead from '../../../../hooks/useReleaseFileForLead';
import { createEmptyGroups, ACCESS_STATE_ORDER } from './data';
import DisplayFilesByLeadPopupContext from '../../../../providers/DisplayFilesByLeadPopup';
import { getDataRoomNodeAccessDisplayState } from '../../utils';

import './DisplayFilesByLead.styles.scss';

type DisplayFilesByLeadProps = {
  className?: string;
  disabled?: boolean;
  leadId: string;
  dataRoomMemberId?: string;
  dataRoomNodesWithStateAndSignal: DataRoomNodeWithStateAndSignal[];
  onSuccessRelease?: (lead: Lead) => void;
};

const DisplayFilesByLead: React.FC<DisplayFilesByLeadProps> = (props) => {
  const {
    className,
    leadId,
    dataRoomMemberId,
    dataRoomNodesWithStateAndSignal,
    disabled = false,
    onSuccessRelease,
  } = props;

  const { getPopupState, setPopupState } = useContext(
    DisplayFilesByLeadPopupContext,
  );
  const popupState = getPopupState?.(leadId);

  const { state, nodes = [] } = popupState || {};

  const [openState, setOpenState] = useState<DataRoomNodeAccessState>(state);
  const [openNodes, setOpenNodes] =
    useState<DataRoomNodeWithStateAndSignal[]>(nodes);

  const classes = classNames('display-files-by-lead', className);

  const releaseFileForLead = useReleaseFileForLead();

  const groupedNodes = useMemo(() => {
    const groups = createEmptyGroups();

    dataRoomNodesWithStateAndSignal.forEach((node) => {
      const displayState = getDataRoomNodeAccessDisplayState(node);

      groups[displayState].push(node);
    });

    return groups;
  }, [dataRoomNodesWithStateAndSignal]);

  const handleOpen = useCallback(
    (state: DataRoomNodeAccessState) => {
      if (openState === state) return;

      setOpenState(state);
      setOpenNodes([...groupedNodes[state]]);
    },
    [groupedNodes, openState],
  );

  const handleClose = useCallback(() => {
    setOpenState(null);
    setOpenNodes([]);
  }, []);

  const handleCycleAccess = useCallback(
    async (node: DataRoomNodeWithStateAndSignal) => {
      if (disabled) return;

      const { dataRoomNodeId, dataRoomNode } = node;

      await releaseFileForLead({
        leadId,
        dataRoomMemberId,
        dataRoomNodeId,
        name: dataRoomNode?.name,
        node,
        onSuccessRelease,
      });
    },
    [disabled, leadId, dataRoomMemberId, onSuccessRelease, releaseFileForLead],
  );

  useEffect(() => {
    if (!openState) return;

    const nodesById = new Map(
      dataRoomNodesWithStateAndSignal.map((node) => [
        node.dataRoomNodeId,
        node,
      ]),
    );

    setOpenNodes((prev) =>
      prev.map((node) => {
        const updated = nodesById.get(node.dataRoomNodeId);
        return updated ? { ...node, ...updated } : node;
      }),
    );
  }, [dataRoomNodesWithStateAndSignal, openState]);

  useEffect(() => {
    setPopupState?.(leadId, {
      state: openState,
      nodes: openNodes,
    });
  }, [leadId, openNodes, openState, setPopupState]);

  return (
    <div className={classes}>
      {ACCESS_STATE_ORDER.map((state) => {
        const isCurrentState = openState === state;

        return (
          <DisplayFilesByLeadCategory
            key={state}
            state={state}
            leadId={leadId}
            disabled={disabled}
            nodes={isCurrentState ? openNodes : []}
            count={getArrayLength(groupedNodes[state])}
            onCycleAccess={handleCycleAccess}
            onClose={handleClose}
            onOpen={() => handleOpen(state)}
          />
        );
      })}
    </div>
  );
};

export default DisplayFilesByLead;
